import React, { useEffect, useRef } from 'react';
import { ChatItemView } from '../../hooks/useTeamChatSocket';
import type { CardRef, VoiceConversationCostRollup } from '../../client';
import type { MentionableAgent } from '../../lib/mentions';
import type { WikiRefProject } from '../../lib/wiki-refs';
import ChatItem from './ChatItem';
import type { VoiceCostState } from './voiceCost';

interface ChatMessageListProps {
  items: ChatItemView[];
  /** Project card references for #KEY-N display expansion */
  cardRefs?: CardRef[];
  /** The team's projects, so wiki:KEY/path and [[KEY:path]] resolve */
  projects?: WikiRefProject[];
  /** Agent roster, so @mentions can link to each agent's details page */
  agents?: MentionableAgent[];
  /** Whether the current scope permits invoking agents. */
  agentsEnabled?: boolean;
  /** Non-visible first-message target selected before a conversation exists. */
  targetAgentName?: string;
  teamId?: string;
  /** The open conversation's id — needed to fetch a tool call's details */
  conversationId?: number;
  /** Answer a seek-feedback / permission prompt (resumes the asking agent) */
  onRespond?: (text: string) => void;
  /** Persist a permission prompt's grant/decline on its message */
  onResolveInteraction?: (
    messageId: number,
    status: 'granted' | 'declined',
  ) => void;
  /**
   * A trailing system row rendered after the last item, INSIDE the scroll
   * container — for standing facts about the conversation (the work-stream
   * pointer) that belong at the end of the transcript rather than floating
   * in the gap above the composer.
   */
  footer?: React.ReactNode;
  /** Content placed immediately before a specific chronological item. */
  beforeItem?: (item: ChatItemView) => React.ReactNode;
  voiceCost?: VoiceConversationCostRollup | null;
  voiceCostState?: VoiceCostState;
}

/** Scrollable transcript; follows the newest content unless the user scrolled up */
const ChatMessageList: React.FC<ChatMessageListProps> = ({
  items,
  cardRefs,
  projects,
  agents,
  agentsEnabled = true,
  targetAgentName,
  teamId,
  conversationId,
  onRespond,
  onResolveInteraction,
  footer,
  beforeItem,
  voiceCost,
  voiceCostState,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true);

  // Newest interaction-carrying item: questions are only answerable there
  // (unresolved permission requests stay actionable — see InteractionPrompt)
  const lastInteractionIndex = items.reduce(
    (last, item, index) => (item.interaction ? index : last),
    -1,
  );

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;
    pinnedToBottomRef.current =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      60;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container && pinnedToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [items, footer]);

  return (
    <div
      className="chat-message-list"
      ref={containerRef}
      onScroll={handleScroll}>
      {items.length === 0 && (
        <div className="chat-empty-state">
          {agentsEnabled
            ? targetAgentName
              ? `Enter a message to start your conversation with ${targetAgentName}.`
              : 'Start conversation with humans or agents on your team. Use the @ symbol to direct your messages, and have the agents respond.'
            : 'Agents are disabled by this team’s Default Work Style. You can still message human teammates here.'}
        </div>
      )}
      {items.map((item, index) => (
        <React.Fragment key={item.key}>
          {beforeItem?.(item)}
          <ChatItem
            item={item}
            cardRefs={cardRefs}
            projects={projects}
            agents={agents}
            teamId={teamId}
            conversationId={conversationId}
            onRespond={onRespond}
            interactionNewest={index === lastInteractionIndex}
            onResolveInteraction={onResolveInteraction}
            voiceCost={voiceCost}
            voiceCostState={voiceCostState}
          />
        </React.Fragment>
      ))}
      {footer}
    </div>
  );
};

export default ChatMessageList;
